module Client.Referral

open Fable.Core
open Fable.Import
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Shared.Domain.ReferralForms
open Style
open System
open Fable.Core.JsInterop
open Fetch
open Shared

type ReferralState =
    | NotStarted
    | CoreDetails
    | Demographics
    | Scores
    | SubstanceUse
    | Summary
    | ConfirmationEmailSent

type Msg =
    | SetPostcodeTest of string
    | SetForename of string
    | SetSurname of string
    | SetGender of Gender
    | SetDateOfBirth of string
    | SetEmailAddress of string
    | SetAddress of string
    | SetTelephoneNumber of string
    | ToggleOkayToLeaveMessage
    | ReferralInfoLoaded of ReferralInfo
    | DrugToggled of DrugName
    | ProceedToDemographics
    | ProceedToCoreDetails
    | ProceedToScores
    | ProceedToSubstanceUse
    | ProceedToSummary
    | RestartWizard
    | FetchError of exn
    | SetSexuality of char option
    | SetNationality of string option
    | SetEthnicity of string option
    | SetDisability of string option
    | SetParental of int option
    | SetEmployment of int option
    | SetPsychologicalHealth of int
    | SetPhysicalHealth of int
    | SetOverallHealth of int
    | SetAgeFirstUsedAlcohol of int option
    | SetSmoking of string
    | SetSubstanceUse of string
    | SetConcerns of string
    | SetHabits of string
    | SetHowOftenDrink of int option
    | SetHowMuchDrink of int option
    | SetBingeFrequency of int option
    | SubmitReferral
    | ReferralResultReceived of Validated
    | GoToHome
    | GetTenantDisplayName
    | RetrievedTenantDisplayName of string

type Model = 
    { 
        Referral: SelfReferralForm
        State: ReferralState
        AllowedPostcodes: string array
        ServicePhoneNumber: string
        PolicyUrl: string option
        Pathways: DrugConfig array
        PostcodeAllowed: bool
        PostcodeErrors: string list
        DateOfBirthErrors: string list
        EmailErrors: string list
        Audit: AlcoholAuditDomain.AlcoholAuditAnswers option
        Waiting: bool
        SubmissionErrors: string list
        Tenant: string
        TenantDisplayName: string
    }

let getReferralInfoCmd tenant =
    let url = Urls.Referral tenant
    FetchRequest.get url ReferralInfoLoaded FetchError

let submitReferralCmd (tenant: string, referral: ReferralFormSet) = 
    let url = Urls.Referral tenant
    FetchRequest.post url referral ReferralResultReceived FetchError

let getTenantDisplayName tenant =
    promise {
        let url = Urls.TenantName tenant
        let props = [ Method HttpMethod.GET ]

        let! response = Fetch.fetch url props
        return! response.text()
    }

let getTenantDisplayNameCmd tenant = Cmd.OfPromise.either getTenantDisplayName tenant RetrievedTenantDisplayName FetchError


let init tenant (audit: Domain.AlcoholAuditDomain.AlcoholAuditAnswers option) = 
    {
        Referral = ReferralForms.zero DateTime.Now
        State = NotStarted
        PostcodeAllowed = true
        ServicePhoneNumber = ""
        AllowedPostcodes = [||]
        Pathways = [||]
        DateOfBirthErrors = []
        PostcodeErrors = []
        EmailErrors = []
        SubmissionErrors = []
        Audit = audit
        Waiting = false
        Tenant = tenant
        TenantDisplayName = tenant
        PolicyUrl = None
    }, Cmd.batch [ getReferralInfoCmd tenant; getTenantDisplayNameCmd tenant ]

open Utils
open Elmish.Navigation

let unwrapValidations =
    function
    | Success -> []
    | Failure f -> f

let inline private findPostcode allowed (code: string) : bool =
    let lcode = code.ToLower()
    Array.exists (fun (x: string) -> 
        lcode.StartsWith(x.ToLower())) allowed

let update (msg:Msg) (model: Model) : Model*Cmd<Msg>* string option = 
    Browser.Dom.console.log("New Referral Msg: ", msg)
    let inline checkPostcode code =
        model.AllowedPostcodes.Length = 0 
            || emptyString code 
            || findPostcode (model.AllowedPostcodes) code
    
    match msg with
    | GoToHome -> model, Navigation.newUrl (Pages.toHash Pages.Page.Home), None
    | SetPostcodeTest code -> 
        Browser.Dom.console.log("SetPostcodeTest: value is ", code)
        { model with 
            Referral = 
                { model.Referral with Postcode = code }
            PostcodeAllowed = checkPostcode code
            PostcodeErrors = 
                code 
                |> SharedValidations.postcodeValidations 
                |> unwrapValidations 
        }, [], None
    | SetForename name -> { model with Referral = { model.Referral with Forename = name }; }, [], None
    | SetSurname name -> { model with Referral = { model.Referral with Surname = name }; }, [], None
    | SetGender gender -> { model with Referral = { model.Referral with Gender = gender }; }, [], None
    | SetNationality nat -> { model with Referral = { model.Referral with Nationality = nat }; }, [], None
    | SetSexuality sex -> { model with Referral = { model.Referral with Sexuality = Option.map (sprintf "%c") sex }; }, [], None
    | SetSmoking smoke -> { model with Referral = { model.Referral with Smoking = smoke }; }, [], None
    | SetSubstanceUse sub -> { model with Referral = { model.Referral with SubstanceUseDescription = sub }; }, [], None
    | SetDisability dis -> { model with Referral = { model.Referral with Disability = dis }; }, [], None
    | SetEthnicity nat -> { model with Referral = { model.Referral with Ethnicity = nat }; }, [], None
    | SetParental p -> { model with Referral = { model.Referral with Parental = p }; }, [], None
    | SetEmployment emp -> { model with Referral = { model.Referral with Employment = emp }; }, [], None
    | SetTelephoneNumber number -> { model with Referral = { model.Referral with TelephoneNumber = number }; }, [], None
    | SetAddress address -> { model with Referral = { model.Referral with Address = address }; }, [], None
    | SetPsychologicalHealth health -> { model with Referral = { model.Referral with PsychologicalHealth = health }; }, [], None
    | SetPhysicalHealth health -> { model with Referral = { model.Referral with PhysicalHealth = health }; }, [], None
    | SetOverallHealth health -> { model with Referral = { model.Referral with OverallHealth = health }; }, [], None
    | SetAgeFirstUsedAlcohol age -> { model with Referral = { model.Referral with AgeFirstUsedAlcohol = age }; }, [], None
    | SetConcerns conc -> { model with Referral = { model.Referral with ConcernedAboutUsage = conc }; }, [], None
    | SetHabits habits -> { model with Referral = { model.Referral with PersonalHabitsAndChanges = habits }; }, [], None
    | ToggleOkayToLeaveMessage -> { model with Referral = { model.Referral with OkayToLeaveAMessage = not model.Referral.OkayToLeaveAMessage }; }, [], None
    | SetEmailAddress email -> { model with Referral = { model.Referral with EmailAddress = email; }; EmailErrors = email |> SharedValidations.emailValidations |> unwrapValidations }, [], None
    | SetDateOfBirth str -> 
        match DateTime.TryParse str with
        | true, dob ->
            { model with Referral = { model.Referral with DateOfBirth = dob }; DateOfBirthErrors = dob |> ReferralForms.dateOfBirthValidations |> unwrapValidations  }, [], None
        | false, _ ->
            { model with DateOfBirthErrors = [sprintf "Couldn't recognise '%s' as a date." str] }, [], None
    | ReferralInfoLoaded info -> 
        { model with 
            AllowedPostcodes = info.AllowedPostcodes; 
            PostcodeAllowed = checkPostcode model.Referral.Postcode; 
            Pathways = info.DrugPathways; 
            ServicePhoneNumber = info.ContactNumber
            PolicyUrl = if info.PolicyUrl = "" then None else Some info.PolicyUrl
        }, [], None
    | DrugToggled drug -> 
        let uses = 
            match drug with
            | DrugName.Alcohol -> { model.Referral with UsesAlcohol = not model.Referral.UsesAlcohol }
            | DrugName.Amphetamines -> { model.Referral with UsesAmphetamines = not model.Referral.UsesAmphetamines }
            | DrugName.Cocaine -> { model.Referral with UsesCocaine = not model.Referral.UsesCocaine }
            | DrugName.Ecstasy -> { model.Referral with UsesEcstasy = not model.Referral.UsesEcstasy }
            | DrugName.Ketamine -> { model.Referral with UsesKetamine = not model.Referral.UsesKetamine }
            | DrugName.Cannabis -> { model.Referral with UsesCannabis = not model.Referral.UsesCannabis }
            | DrugName.CrackCocaine -> { model.Referral with UsesCrackCocaine = not model.Referral.UsesCrackCocaine }
            | DrugName.PrescribedOrOverTheCounter -> { model.Referral with UsesPrescribedOrOverTheCounter = not model.Referral.UsesPrescribedOrOverTheCounter }
            | DrugName.Spice -> { model.Referral with UsesSpice = not model.Referral.UsesSpice }
            | DrugName.OtherDrug -> { model.Referral with UsesOtherDrug = not model.Referral.UsesOtherDrug }
            | DrugName.Opiates -> { model.Referral with UsesOpiates = not model.Referral.UsesOpiates }
            | _ -> failwith "Not coded"
        { model with Referral = uses }, [], None
    | ProceedToCoreDetails ->
        { model with State = CoreDetails }, [], None
    | ProceedToDemographics ->
        { model with State = Demographics }, [], None
    | RestartWizard ->
        { model with State = NotStarted }, [], None
    | ProceedToScores ->
        { model with State = Scores }, [], None
    | ProceedToSubstanceUse ->
        { model with State = SubstanceUse }, [], None
    | ProceedToSummary ->
        { model with State = Summary; SubmissionErrors = [] }, [], None
    | SetBingeFrequency freq ->
        { model with Referral = { model.Referral with AlcoholBingeFrequency = freq } }, [], None
    | SetHowMuchDrink amt ->
        { model with Referral = { model.Referral with DrinkingDayUnits = amt } }, [], None
    | SetHowOftenDrink oft ->
        { model with Referral = { model.Referral with HowOftenDrink = oft } }, [], None
    | FetchError exn -> model, [], None
    | SubmitReferral ->
        { model with Waiting = true; }, submitReferralCmd (model.Tenant, { SelfReferral = model.Referral; AlcoholAudit = model.Audit }), None
    | ReferralResultReceived result ->
        match result with
        | Success ->
            { model with State = ConfirmationEmailSent; Waiting = false }, [], Some "Please check for your confirmation email."
        | Failure errors ->
            { model with SubmissionErrors = errors; }, [], None
    | GetTenantDisplayName ->
        model, getTenantDisplayNameCmd model.Tenant, None
    | RetrievedTenantDisplayName name ->
        { model with TenantDisplayName = name }, [], None

let renderSubstance drug name display dispatch bind (model: Model) =
    let warn = model.Pathways |> Array.exists ((=){ Name = drug; Behaviour = Behaviour.NoRefer })
    div [
            ClassName ("form-group" + (if warn then " has-warning" else "")); 
            Style [
                Display DisplayOptions.Flex
                AlignItems AlignItemsOptions.Center
                FlexWrap "wrap"
            ]
        ] [
        label [ HtmlFor name; ClassName "control-label col-sm-5 col-xs-6" ] [ str display ]
        div [ ClassName "col-sm-1" ] [
            yield input [ 
                HTMLAttr.Type "checkbox"; 
                ClassName "form-control"; 
                Id name; 
                OnChange (fun ev -> drug |> DrugToggled |> dispatch);
                Checked bind
                Disabled warn
            ]
        ]
        div [ ClassName "col-sm-6 col-xs-12" ] [
            if warn then
                yield span [ ClassName "help-block" ] [ str <| sprintf "Please call the service on %s." model.ServicePhoneNumber ]
        ]
    ]

let formLabel id mandatory lbl = 
    label [
        HtmlFor id
        ClassName ("control-label" + if mandatory then " mandatory" else "")
    ] [
        str lbl
    ]

let renderValidations (errs: string list) = seq {
    if errs.Length > 0 then
        yield span [ ClassName "help-block" ] [
            for error in errs do
                yield str error
                yield br []
        ]
}

let toChar = FSharp.Core.Option.map (fun (s: string) -> s.[0])
let toInt (s: string option) = FSharp.Core.Option.map int s

let demoList id lbl setter dispatch items v =
    formGroup [
        formLabel id false lbl
        select [
            Id id
            ClassName "form-control"
            OnChange(fun ev -> (match !!ev.target?value with "" -> None | x -> Some x ) |> setter |> dispatch)
            DefaultValue v
        ] [
            for item in items do
                yield option [ 
                    HTMLAttr.Value (fst item)
                ] [ 
                    str <| snd item 
                ]            
        ]
    ]

let renderDemographics (model: Model) (dispatch: Msg -> unit) = seq {
    yield h2 [] [ str "Demographics" ]
    yield p [ ClassName "alert alert-info" ] [
        str "Everything in this section is optional, though anything you enter will be used to improve how we work with you."
    ]
    yield form [ ClassName "form" ] [
        demoList "sexuality" "Sexuality" (toChar>>SetSexuality) dispatch sexualities (optString model.Referral.Sexuality)
        demoList "ethnicity" "Ethnicity" SetEthnicity dispatch ethnicities (optString model.Referral.Ethnicity)
        demoList "nationality" "Nationality" SetNationality dispatch nationalities (optString model.Referral.Nationality)
        demoList "disability" "Disability" SetDisability dispatch disabilities (optString model.Referral.Disability)
        demoList "parental" "Parental" (toInt>>SetParental) dispatch parentalStatuses (optString model.Referral.Parental)
        demoList "employment" "Employment" (toInt>>SetEmployment) dispatch employmentStatuses (optString model.Referral.Employment)
    ]
    yield button [ 
        ClassName "btn btn-primary"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToScores)
    ] [
        str "Proceed"
    ] 
    yield button [ 
        ClassName "btn btn-default"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToCoreDetails)
    ] [
        str "Back"
    ]
}

let renderScoreBar id (score: int) setter dispatch =
    div [ ClassName "input-group" ] [ 
        span [ ClassName "input-group-addon" ] [ str <| score.ToString() ]
        input [
            ClassName "form-control"
            HTMLAttr.Type "range"
            Min "0"
            Max "10"
            Id id
            Elmish.React.Helpers.valueOrDefault (score.ToString())
            OnChange (fun ev -> !!ev.target?valueAsNumber |> setter |> dispatch)
        ]
    ]

let renderSummary (model: Model) (dispatch: Msg -> unit) = seq {
    if model.Waiting then
        yield h2 [ ClassName "animated-background centerflex" ] [ str "Please wait..." ]
    else
        yield h2 [] [ str "Referral Summary" ]
        yield p [] [
            str "You are about to submit your details to "
            str model.TenantDisplayName
            str " via this online referral tool. After choosing to proceed, 
            you will be sent an email that contains a link you must click on to confirm the referral. "
            br []
            str "Your email address is "
            strong [] [ str model.Referral.EmailAddress ]
            str ". Please ensure this is correct as that is where we will send the confirmation email."
        ]
        match model.PolicyUrl with
        | None -> ()
        | Some url ->
            yield p [ ClassName "alert alert-info" ] [
                str "We keep your personal information safe and secure, and maintain your privacy rights in line with the Data Protection Act 2018. For more information about how we manage your data, please see our "
                a [ Href url; Alt "Privacy Policy" ] [ str "Privacy Policy" ]
            ]

        if model.SubmissionErrors.Length > 0 then
            yield p [ ClassName "alert alert-danger" ] (model.SubmissionErrors |> List.map (fun x -> div [] [ str x ]))
       
        yield button [ 
            ClassName "btn btn-success"
            HTMLAttr.Type "button"
            OnClick (fun _ -> dispatch SubmitReferral)
        ] [
            str "Submit Referral"
        ]
        yield button [ 
            ClassName "btn btn-default"
            HTMLAttr.Type "button"
            OnClick (fun _ -> dispatch ProceedToSubstanceUse)
        ] [
            str "Back"
        ]  
}

let renderConfirmationSent (model: Model) (dispatch: Msg -> unit) = seq {
    yield h2 [] [ str "Referral Sent" ]
    yield p [] [
        str "The email has been sent to "
        strong [] [ str model.Referral.EmailAddress ]
        str ". Once you have clicked the link in the email, the referral will be sent."
    ]
    yield p [ ClassName "alert alert-info" ] [
        str "By confirming your referral you are consenting to the processing of your data in order to fulfil your request."
    ]
   
    yield button [ 
        ClassName "btn btn-default"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch GoToHome)
    ] [
        str "Back to Home"
    ]  
}

let renderScores (model: Model) (dispatch: Msg -> unit) = seq {
    yield h2 [] [ str "How's Life?" ]
    yield p [ ClassName "alert alert-info" ] [
        str "Rate yourself on the following between 0 and 10 (0 = terrible, 10 = fabulous)."
    ]
    yield form [ ClassName "form" ] [
        formGroup [
            formLabel "psychological" false "Psychological"
            renderScoreBar "psychological" (model.Referral.PsychologicalHealth) SetPsychologicalHealth dispatch
            span [ ClassName "help-block" ] [ str "Emotions, feelings" ]
        ]
        formGroup [
            formLabel "health" false "Physical"
            renderScoreBar "health" (model.Referral.PhysicalHealth) SetPhysicalHealth dispatch
            span [ ClassName "help-block" ] [ str "How well are you?" ]
        ]
        formGroup [
            formLabel "overall" false "Overall"
            renderScoreBar "overall" (model.Referral.OverallHealth) SetOverallHealth dispatch
            span [ ClassName "help-block" ] [ str "Can you enjoy your life?" ]
        ]
    ]
    yield button [ 
        ClassName "btn btn-primary"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToSubstanceUse)
    ] [
        str "Proceed"
    ]
    yield button [ 
        ClassName "btn btn-default"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToDemographics)
    ] [
        str "Back"
    ]  
}

let renderSubstanceUse (model: Model) (dispatch: Msg -> unit) = seq {
    yield h2 [] [ str "Alcohol and Drug Use" ]
    match model.Audit with
    | None ->
        yield form [ ClassName "form" ] [
            formGroup [
                formLabel "howoften" false "How often do you have a drink containing alcohol?"
                select [
                    ClassName "form-control"
                    Id "howoften"
                    OnChange (fun ev -> (match !!ev.target?value with "" -> SetHowOftenDrink None | v -> v |> System.Int32.Parse |> Some |> SetHowOftenDrink) |> dispatch);
                    HTMLAttr.Value (optString model.Referral.HowOftenDrink)
                ] [
                    option [ HTMLAttr.Value "" ] [ str "Please select an option" ]
                    option [ HTMLAttr.Value "0" ] [ str "Never" ]
                    option [ HTMLAttr.Value "1" ] [ str "Monthly or less" ]
                    option [ HTMLAttr.Value "2" ] [ str "2-4 times a month" ]
                    option [ HTMLAttr.Value "3" ] [ str "2-3 times a week" ]
                    option [ HTMLAttr.Value "4" ] [ str "4+ times a week" ]
                ]
            ]
            formGroup [
                formLabel "bingeAmount" false "How many units of alcohol do you drink on a typical day when you are drinking?"
                select [
                    ClassName "form-control"
                    Id "bingeAmount"
                    OnChange (fun ev -> (match !!ev.target?value with "" -> SetHowMuchDrink None | v -> v |> System.Int32.Parse |> Some |> SetHowMuchDrink) |> dispatch);
                    HTMLAttr.Value (optString model.Referral.DrinkingDayUnits)
                ] [
                    option [ HTMLAttr.Value "" ] [ str "Please select an option" ]
                    option [ HTMLAttr.Value "0" ] [ str "1-2" ]
                    option [ HTMLAttr.Value "1" ] [ str "3-4" ]
                    option [ HTMLAttr.Value "2" ] [ str "5-6" ]
                    option [ HTMLAttr.Value "3" ] [ str "7-9" ]
                    option [ HTMLAttr.Value "4" ] [ str "10+" ]
                ]
            ]
            formGroup [
                formLabel "bingeFreq" false "How often have you had 6 or more units on a single occasion in the last year?"
                select [
                    ClassName "form-control"
                    Id "bingeFreq"
                    OnChange (fun ev -> (match !!ev.target?value with "" -> SetBingeFrequency None | v -> v |> System.Int32.Parse |> Some |> SetBingeFrequency) |> dispatch);
                    HTMLAttr.Value (optString model.Referral.AlcoholBingeFrequency)
                ] [
                    option [ HTMLAttr.Value "" ] [ str "Please select an option" ]
                    option [ HTMLAttr.Value "0" ] [ str "Never" ]
                    option [ HTMLAttr.Value "1" ] [ str "Less than monthly" ]
                    option [ HTMLAttr.Value "2" ] [ str "Monthly" ]
                    option [ HTMLAttr.Value "3" ] [ str "Weekly" ]
                    option [ HTMLAttr.Value "4" ] [ str "Daily" ]
                ]
            ]
        ] 
    | Some audit ->
        yield p [ ClassName "alert alert-success" ] [ str "You have already completed an alcohol audit." ]
    yield form [ ClassName "form" ] [
        formGroup [
            formLabel "smoke" false "Do you smoke? Daily or socially?"
            textarea [
                ClassName "form-control"
                Id "smoke"
                Placeholder "Enter your smoking behaviour"; 
                OnChange (fun ev -> !!ev.target?value |> SetSmoking |> dispatch);
                DefaultValue model.Referral.Smoking
                MaxLength 500.
                Rows 3
            ] []
        ]
        formGroup [
            formLabel "substanceUse" false "Have you ever used other substances? How often? How old when you first tried them?"
            textarea [
                ClassName "form-control"
                Id "substanceUse"
                Placeholder "Enter your substance usage behaviour"; 
                OnChange (fun ev -> !!ev.target?value |> SetSubstanceUse |> dispatch);
                DefaultValue model.Referral.SubstanceUseDescription
                MaxLength 500.
                Rows 4
            ] []
        ]
        formGroup [
            formLabel "concerns" false "Have you ever been concerned about someone else’s drink or substance use? What concerned you?"
            textarea [
                ClassName "form-control"
                Id "concerns"
                Placeholder "Enter your concerns about someones substance usage"; 
                OnChange (fun ev -> !!ev.target?value |> SetConcerns |> dispatch);
                DefaultValue model.Referral.ConcernedAboutUsage
                MaxLength 500.
                Rows 4
            ] []
        ]
        formGroup [
            formLabel "habits" false "Have you ever recognised those same habits in your own use? What changes would you consider to
your use? (e.g. days off, cut down units, swap for non-alcohol drinks, etc.)"
            textarea [
                ClassName "form-control"
                Id "habits"
                Placeholder "Enter your own usage habit"; 
                OnChange (fun ev -> !!ev.target?value |> SetHabits |> dispatch);
                DefaultValue model.Referral.PersonalHabitsAndChanges
                MaxLength 1000.
                Rows 6
            ] []
        ]
    ]
    yield button [ 
        ClassName "btn btn-primary"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToSummary)
    ] [
        str "Proceed"
    ]
    yield button [ 
        ClassName "btn btn-default"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToScores)
    ] [
        str "Back"
    ]
}

let renderCoreDetails (model: Model) (dispatch: Msg -> unit) = seq {
    yield h2 [] [ str "Details" ]
    yield p [ ClassName "alert alert-info" ] [
        str "First, we need to capture some of your core information. We'll use this to identify, assess and contact you as part of your referral."
    ]
    yield form [ ClassName "form" ] [
        formGroup [
            formLabel "forename" true "Forename"
            textInput "forename" "Enter your forename" SetForename dispatch model.Referral.Forename (Some 50.)
        ]
        formGroup [
            formLabel "surname" true "Surname"
            textInput "surname" "Enter your surname" SetSurname dispatch model.Referral.Surname (Some 50.)
        ]
        formGroupWithErrors (model.DateOfBirthErrors.Length > 0) [
            yield formLabel "dob" true "Date of Birth"
            yield dateInput "dob" "Enter your date of birth" (fun ev -> !!ev.target?value |> SetDateOfBirth |> dispatch) (if model.Referral.DateOfBirth = DateTime.MinValue then "" else formatDate "yyyy-MM-dd" model.Referral.DateOfBirth)
            yield! renderValidations model.DateOfBirthErrors
        ]
        formGroup [
            let getGenderValue =
                function
                | "Male" -> Gender.Male
                | "Female" -> Gender.Female
                | "I'm not sure" -> Gender.NotKnown
                | "I don't want to say" -> Gender.NotSpecified
                | _ -> failwith "not a valid gender"

            yield formLabel "gender" true "Gender"
            yield select [
                Id "gender"
                ClassName "form-control mandatory"
                Placeholder "Enter your gender"
                OnChange(fun ev -> !!ev.target?value |> getGenderValue |> SetGender |> dispatch)
            ] [
                option [] [ str "Male" ]
                option [] [ str "Female" ]
                option [] [ str "I'm not sure" ]
                option [] [ str "I don't want to say" ]
            ]
        ]
        formGroupWithErrors (model.EmailErrors.Length > 0) [
            yield formLabel "email" true "Email Address"
            yield textInput "email" "Enter your email address" SetEmailAddress dispatch model.Referral.EmailAddress (Some 254.)
            yield! renderValidations model.EmailErrors
        ]
        formGroup [
            formLabel "address" false "Address"
            textarea [
                ClassName "form-control"
                Id "address"
                Placeholder "Enter your address"; 
                OnChange (fun ev -> !!ev.target?value |> SetAddress |> dispatch);
                DefaultValue model.Referral.Address
                MaxLength 140.
                Rows 4
            ] []
        ]
        formGroup [
            formLabel "telephone" false "Telephone Number"
            textInput "telephone" "Enter your telephone number" SetTelephoneNumber dispatch model.Referral.TelephoneNumber (Some 20.)
        ]
        formGroup [
            formLabel "leaveMessage" false "Okay to leave a message?"
            input [ 
                HTMLAttr.Type "checkbox"; 
                ClassName "form-control"; 
                Id "leaveMessage"; 
                OnChange (fun ev -> ToggleOkayToLeaveMessage |> dispatch);
                Checked model.Referral.OkayToLeaveAMessage
            ]
            span [ ClassName "help-block" ] [ str "If checked we may also leave you voicemail messages and text messages as well as phone calls and emails." ]
        ]
        
    ]
    yield button [ 
        ClassName "btn btn-primary"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch ProceedToDemographics)
        Disabled (model.Referral.Forename.Length = 0 
            || model.Referral.Surname.Length = 0 
            || model.DateOfBirthErrors.Length > 0 
            || model.Referral.DateOfBirth = DateTime.MinValue
            || model.EmailErrors.Length > 0
            || model.Referral.EmailAddress.Length = 0)
    ] [
        str "Proceed"
    ]
    yield button [ 
        ClassName "btn btn-default"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch RestartWizard)
    ] [
        str "Back"
    ]
}

let renderSplash (model: Model) dispatch = seq {
    yield h2 [] [ str "Prerequisites" ]
    yield p [ ClassName "alert alert-info" ] [
        str "Before we start, we need to collect some information from you to see if we can help. All fields "
        span [ ClassName "mandatory" ] [ str "marked" ]
        str " are required. Information provided here will only be viewed and processed in line with GDPR. 
        By submitting data here you give consent to the service to retain this information."
    ]

    match model.Audit with
    | None -> ()
    | Some audit ->
        yield p [ ClassName "alert alert-success" ] [
            str <| sprintf "Your alcohol audit information is already included in this submission. You scored %d points." (AlcoholAuditDomain.calculateAuditScore audit)
        ]
        
    if model.AllowedPostcodes.Length > 0 then
        yield div [] [
            form [ ClassName "form" ] [
                formGroup [
                    formGroupWithErrors (model.PostcodeErrors.Length > 0) [
                        yield formLabel "postcode" true "Postcode"
                        yield textInput 
                            "postcode" 
                            "Enter your postcode." 
                            SetPostcodeTest
                            dispatch 
                            (model.Referral.Postcode) 
                            (Some 8.)
                        yield! renderValidations model.PostcodeErrors
                    ]
                    span [ ClassName "help-block" ] [
                        yield str "This service has a limited catchment area. Please provide your postcode (including the space) to determine if we can help."
                        if model.PostcodeAllowed = false then
                            yield strong [] [
                                str <| sprintf "This postcode is outside of the catchment area. If you need help, call the service on %s." model.ServicePhoneNumber
                            ]
                    ]
                ]
            ]
        ]

    if model.Pathways.Length > 0 then

        match model.Audit with
        | Some audit -> ()
        | None ->           
            if model.Pathways |> Array.exists ((=){ Name = DrugName.Alcohol; Behaviour = Behaviour.Audit }) then
                yield h3 [] [ str "Alcohol Audit" ]
                yield div [] [
                     p [ ClassName "" ] [ 
                         str "If you would like to receive some help with your drinking, please go to "
                         a [ Href <| Pages.toHash (Pages.Page.AlcoholAudit model.Tenant) ] [ str "this page" ]
                         str " to record your drinking habits."
                    ]
                ]

        yield div [] [
            h3 [ ClassName "mandatory" ] [ str "Which substances do you want help with?" ]
            form [ ClassName "form-horizontal" ] [
                renderSubstance DrugName.Alcohol "alcohol" "Alcohol" dispatch model.Referral.UsesAlcohol model
                renderSubstance DrugName.Amphetamines "amphetamines" "Amphetamines" dispatch model.Referral.UsesAmphetamines model
                renderSubstance DrugName.Cocaine "cocaine" "Cocaine" dispatch model.Referral.UsesCocaine model
                renderSubstance DrugName.Ecstasy "ecstasy" "Ecstasy/MDMA" dispatch model.Referral.UsesEcstasy model
                renderSubstance DrugName.Ketamine "ketamine" "Ketamine" dispatch model.Referral.UsesKetamine model
                renderSubstance DrugName.Cannabis "cannabis" "Cannabis" dispatch model.Referral.UsesCannabis model
                renderSubstance DrugName.CrackCocaine "crack" "Crack Cocaine" dispatch model.Referral.UsesCrackCocaine model
                renderSubstance DrugName.PrescribedOrOverTheCounter "prescribed" "Prescribed/Over-the-Counter Medicine" dispatch model.Referral.UsesPrescribedOrOverTheCounter model
                renderSubstance DrugName.Spice "spice" "Novel Psychoactive Substances/Spice" dispatch model.Referral.UsesSpice model
                renderSubstance DrugName.OtherDrug "other" "Other Drug" dispatch model.Referral.UsesOtherDrug model
                renderSubstance DrugName.Opiates "opiates" "Opiates" dispatch model.Referral.UsesOpiates model
            ]                  
        ]

    yield button [ 
        ClassName "btn btn-primary"
        HTMLAttr.Type "button"
        Disabled (model.Referral.Postcode.Length = 0
            || not model.PostcodeAllowed
            || model.PostcodeErrors.Length > 0
            || (model.Pathways.Length > 0 && not <| ReferralForms.usesAny model.Referral)) 
        OnClick (fun _ -> dispatch ProceedToCoreDetails)
    ] [
        str "Proceed"
    ]
}

let view (model: Model) (dispatch: Msg -> unit) =
    [
        div [ ClassName "container" ] [
            yield div [ 
                ClassName "centerflex" 
                Id "alcoholAudit"
            ] [
                div [ 
                    ClassName "formstyle"
                ] [
                    match model.State with
                    | NotStarted -> yield! renderSplash model dispatch
                    | CoreDetails -> yield! renderCoreDetails model dispatch
                    | Demographics -> yield! renderDemographics model dispatch
                    | Scores -> yield! renderScores model dispatch
                    | SubstanceUse -> yield! renderSubstanceUse model dispatch
                    | Summary -> yield! renderSummary model dispatch
                    | ConfirmationEmailSent -> yield! renderConfirmationSent model dispatch
                ]
            ]
        ]
    ]
